"use client";

import { createChallenge } from "@/actions/match";
import { useFeedbackPrompt } from "@/contexts/FeedbackPromptContext";
import {
  useMatchesContext,
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { useAllTeamStatistics } from "@/hooks/useAllTeamStatistics";
import { useCanChallenge } from "@/hooks/useCanChallenge";
import { useTeamsByLevel } from "@/hooks/useTeamsByLevel";
import { logMatchEvent, logTeamEvent } from "@/lib/analytics";
import { getTowerLevel } from "@/lib/tower";
import { ChallengeType, MatchStatus, PublicUser, Team } from "@/types";
import { useProgress } from "@bprogress/next";
import { Button, Select, SelectItem } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { TeamDrawer } from "./team-info/TeamDrawer";
import { TowerLegend } from "./TowerLegend";
import { TowerLevel } from "./TowerLevel";

export function TowerView() {
  const { tournament, players } = useTournamentContext();
  const { teams, currentUserTeam } = useTeamsContext();
  const { matches } = useMatchesContext();
  const teamsByLevel = useTeamsByLevel(teams, tournament);
  // Pre-compute statistics for ALL teams once (avoids N+1 pattern)
  const teamStatsMap = useAllTeamStatistics();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { canChallenge, cardType, vacationStartsInDays } = useCanChallenge(
    currentUserTeam,
    selectedTeam,
    tournament,
    teams,
    matches,
  );
  const progress = useProgress();
  const router = useRouter();
  const t = useTranslations();
  const { trackAction } = useFeedbackPrompt();

  // Convert players Map to array for child components
  const playersArray = useMemo<PublicUser[]>(
    () => Array.from(players.values()),
    [players],
  );

  // Get active match for selected team
  const activeMatch = useMemo(() => {
    if (!selectedTeam) return null;
    return (
      matches.find(
        (match) =>
          (match.team1Id === selectedTeam.id ||
            match.team2Id === selectedTeam.id) &&
          (match.status === MatchStatus.CHALLENGE ||
            match.status === MatchStatus.SCHEDULED),
      ) || null
    );
  }, [selectedTeam, matches]);

  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team);
    setIsDrawerOpen(true);
    logTeamEvent("view", team.id);
    trackAction(); // Track for feedback prompt
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    // Delay clearing selected team to allow drawer animation to complete
    setTimeout(() => setSelectedTeam(null), 400);
  };

  const handleChallenge = async (teamId: string) => {
    if (!currentUserTeam || isSubmitting) return;

    setIsSubmitting(true);
    progress.start();

    try {
      const result = await createChallenge({
        team1Id: currentUserTeam.id,
        team2Id: teamId,
        challengeType: cardType as ChallengeType | undefined,
      });

      if (!result.success) {
        console.error("Challenge failed:", result.error);
        addToast({
          title: t("teams.challengeFailed"),
          description: result.error,
          color: "danger",
        });
      } else {
        addToast({
          title: t("teams.challengeSent", {
            teamName: selectedTeam?.name ?? t("common.unnamedTeam"),
          }),
          color: "success",
        });
        logMatchEvent("create", result.matchId);
        trackAction(); // Track for feedback prompt
        // Automatically navigate to chat with the challenged team.
        // matchId is the same as chatId.
        if (result.matchId && tournament?.slug) {
          router.push(
            `/tournaments/${tournament.slug}/chat/${result.matchId}`,
          );
        }
      }
    } catch (error) {
      console.error("Challenge failed:", error);
      addToast({
        title: t("teams.challengeFailed"),
        color: "danger",
      });
    } finally {
      progress.stop();
      setIsSubmitting(false);
    }
  };

  const selectedTeamPlayers = useMemo<PublicUser[]>(() => {
    if (!selectedTeam) return [];
    return playersArray.filter((p) => selectedTeam.playerIds.includes(p.id));
  }, [selectedTeam, playersArray]);

  const scrollWithOffset = useCallback((element: Element, offset: number) => {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  }, []);

  const scrollToLevel = useCallback(
    (level: number) => {
      const element = document.querySelector(`[data-level="${level}"]`);
      if (element) {
        scrollWithOffset(element, 90);
      }
    },
    [scrollWithOffset],
  );

  const scrollToMyTeam = useCallback(() => {
    const element = document.querySelector('[data-my-team="true"]');
    if (element) {
      scrollWithOffset(element, 190);
    }
  }, [scrollWithOffset]);

  const myTeamLevel = useMemo(() => {
    if (!currentUserTeam || !tournament) return null;

    // Find team's position in sorted active teams array (1-based)
    const sortedActiveTeams = [...teams]
      .filter((t) => t.isActive && t.isPlacedInTower !== false)
      .sort((a, b) => a.ranking - b.ranking);

    const position =
      sortedActiveTeams.findIndex((t) => t.id === currentUserTeam.id) + 1;

    if (position === 0) return null; // Team not found or not active

    return getTowerLevel(
      position,
      tournament.towerTeamsPerRow,
      tournament.towerTopRowTeamCount,
    );
  }, [currentUserTeam, teams, tournament]);

  if (!tournament) {
    return null;
  }

  return (
    <>
      {/* SVG symbol library — referenced by <use> in fantasy-style team cards */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
      >
        <defs>
          <symbol id="fc-cn" viewBox="0 0 16 16">
            <path
              d="M2 2 L8 2 M2 2 L2 8 M5 2 Q5 5 8 5 Q8 8 5 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              strokeLinecap="round"
            />
            <circle cx="2.5" cy="2.5" r="0.9" fill="currentColor" />
          </symbol>
          <symbol id="fc-ts" viewBox="0 0 24 24">
            <path
              d="M5 22 L5 10 L8 10 L8 7 L12 4 L16 7 L16 10 L19 10 L19 22 Z M9 22 L9 16 L15 16 L15 22 M7 10 L7 8 M11 10 L11 8 M13 10 L13 8 M17 10 L17 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </symbol>
          {/* Challenge button gradients — consumed by ChallengeButton.tsx */}
          <linearGradient id="fc-ch-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f5a070" />
            <stop offset="18%" stopColor="#e06038" />
            <stop offset="52%" stopColor="#a02818" />
            <stop offset="100%" stopColor="#4a0c08" />
          </linearGradient>
          <linearGradient id="fc-ch-body-joker" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d8a8f0" />
            <stop offset="18%" stopColor="#a060c8" />
            <stop offset="52%" stopColor="#5a1f88" />
            <stop offset="100%" stopColor="#1a0830" />
          </linearGradient>
          <linearGradient
            id="fc-ch-body-braveheart"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#ff9aa9" />
            <stop offset="18%" stopColor="#d04060" />
            <stop offset="52%" stopColor="#8a1830" />
            <stop offset="100%" stopColor="#2a0510" />
          </linearGradient>
          <linearGradient
            id="fc-ch-body-disabled"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#9a9a9a" />
            <stop offset="18%" stopColor="#666" />
            <stop offset="52%" stopColor="#363636" />
            <stop offset="100%" stopColor="#161616" />
          </linearGradient>
          <linearGradient id="fc-ch-dark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a3e" />
            <stop offset="50%" stopColor="#0a1c22" />
            <stop offset="100%" stopColor="#040e14" />
          </linearGradient>
          <linearGradient id="fc-ch-hl" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,225,190,0.55)" />
            <stop offset="100%" stopColor="rgba(255,225,190,0)" />
          </linearGradient>
          <radialGradient id="fc-ch-gem" cx="0.3" cy="0.3">
            <stop offset="0%" stopColor="#c8e8e0" />
            <stop offset="45%" stopColor="#4ca89c" />
            <stop offset="100%" stopColor="#1a4a44" />
          </radialGradient>
          <radialGradient id="fc-ch-rivet" cx="0.3" cy="0.3">
            <stop offset="0%" stopColor="#a8e0d2" />
            <stop offset="55%" stopColor="#4ca89c" />
            <stop offset="100%" stopColor="#1a3a36" />
          </radialGradient>
        </defs>
      </svg>
      <div className="w-full flex flex-col gap-3 sm:gap-6 md:gap-8">
        {/* Level Navigation */}
        {teamsByLevel.length > 1 && (
          <nav
            aria-label={t("tower.levelNavigation")}
            className="sticky top-0 z-10 bg-background/80 backdrop-blur-md py-2 -mx-2 px-2 sm:-mx-4 sm:px-4"
          >
            <div className="flex gap-2 justify-center items-center flex-wrap">
              {currentUserTeam && myTeamLevel !== null && (
                <Button
                  size="md"
                  color="default"
                  variant="bordered"
                  onPress={scrollToMyTeam}
                  className="flex-shrink-0 border-2 border-amber-400 text-amber-400"
                >
                  {t("tower.myLevel", {
                    level: myTeamLevel,
                  })}
                </Button>
              )}
              {teamsByLevel.length <= 10 ? (
                <div className="flex gap-2 flex-wrap justify-center">
                  {teamsByLevel.map((_, levelIndex) => {
                    const isMyLevel = myTeamLevel === levelIndex + 1;
                    return (
                      <Button
                        key={`nav-level-${levelIndex}`}
                        size="md"
                        color="default"
                        variant={isMyLevel ? "bordered" : "flat"}
                        onPress={() => scrollToLevel(levelIndex + 1)}
                        className={`min-w-[40px] ${isMyLevel ? "border-2 border-amber-400 text-amber-400" : ""}`}
                      >
                        {levelIndex + 1}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <Select
                  aria-label={t("tower.selectLevel")}
                  placeholder={t("tower.selectLevel")}
                  size="md"
                  className="w-40"
                  onChange={(e) => {
                    const level = parseInt(e.target.value, 10);
                    if (!isNaN(level)) {
                      scrollToLevel(level);
                    }
                  }}
                >
                  {teamsByLevel.map((_, levelIndex) => (
                    <SelectItem key={levelIndex + 1}>
                      {t("tower.levelNumber", { level: levelIndex + 1 })}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          </nav>
        )}

        {/* Tower Container */}
        <div className="pb-2 sm:pb-6 md:pb-8">
          <div className="flex flex-col items-center gap-2 sm:gap-6 md:gap-8">
            {teamsByLevel.map((levelTeams, levelIndex) => (
              <div
                key={"level-" + levelIndex}
                data-level={levelIndex + 1}
                className="w-full even:bg-black/20 sm:even:bg-black/50 py-4 sm:py-8 md:py-10 px-0 sm:px-2 rounded-xl sm:rounded-3xl lg:even:bg-transparent"
              >
                <TowerLevel
                  key={levelIndex}
                  level={levelIndex}
                  teams={levelTeams}
                  players={playersArray}
                  currentUserTeam={currentUserTeam}
                  tournament={tournament}
                  teamStatsMap={teamStatsMap}
                  onTeamClick={handleTeamClick}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <TowerLegend />
      </div>

      {/* Team Drawer */}
      <TeamDrawer
        team={selectedTeam}
        myTeamId={currentUserTeam?.id ?? null}
        myTeamName={currentUserTeam?.name}
        players={selectedTeamPlayers}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        canChallenge={canChallenge}
        onChallenge={handleChallenge}
        activeMatch={activeMatch}
        cardType={cardType}
        isSubmitting={isSubmitting}
        vacationStartsInDays={vacationStartsInDays}
      />

      {/* Confirmation modal */}
    </>
  );
}
